import {
  doc,
  collection,
  setDoc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { createShuffledDeck, compareCardIds } from '@/utils/playingCards';

/** Subcollection under games/{gameId}; document id is always `session`. */
export function sessionDocRef(db, gameId) {
  return doc(db, 'games', gameId, 'selectOMatic', 'session');
}

/**
 * One doc per team. Sibling to selectOMatic collection (not nested under
 * selectOMatic/session) so paths stay valid: docs need an even # of segments.
 */
export function participantDocRef(db, gameId, teamId) {
  return doc(db, 'games', gameId, 'selectOMaticParticipants', teamId);
}

export function selectOMaticParticipantsCollection(db, gameId) {
  return collection(db, 'games', gameId, 'selectOMaticParticipants');
}

/** Cumulative Select-o-Matic wins per team for this game (top two per ended session). */
export function selectOMaticWinsCollection(db, gameId) {
  return collection(db, 'games', gameId, 'selectOMaticWins');
}

export async function startSelectOMaticSession(db, gameId) {
  const sessionRef = sessionDocRef(db, gameId);
  const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const deck = createShuffledDeck();
  await setDoc(sessionRef, {
    active: true,
    sessionId,
    deck,
    startedAt: serverTimestamp(),
  });
  return sessionId;
}

export async function endSelectOMaticSession(db, gameId) {
  const sessionRef = sessionDocRef(db, gameId);
  await setDoc(sessionRef, { active: false }, { merge: true });
}

/**
 * Ends the session and awards one win to each of the top two teams (by chosen card)
 * for the current sessionId. Idempotent per session (awardedForSessionId on session doc).
 */
export async function finalizeSelectOMaticSession(db, gameId) {
  const sessionRef = sessionDocRef(db, gameId);
  const sessionSnap = await getDoc(sessionRef);
  if (!sessionSnap.exists()) {
    return { awarded: [], sessionEnded: true };
  }

  const sessionData = sessionSnap.data();
  const sessionIdAtStart = sessionData.sessionId || '';

  let topTwo = [];
  if (sessionIdAtStart) {
    const partsSnap = await getDocs(selectOMaticParticipantsCollection(db, gameId));
    const ranked = partsSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(
        (p) =>
          p.sessionId === sessionIdAtStart &&
          !p.declined &&
          p.chosenCard != null &&
          p.chosenCard !== ''
      )
      .sort((a, b) => compareCardIds(Number(b.chosenCard), Number(a.chosenCard)));
    topTwo = ranked.slice(0, 2).map((p) => ({
      teamId: String(p.teamId ?? p.id),
      teamName: p.teamName || 'Team',
    }));
  }

  await runTransaction(db, async (t) => {
    const sSnap = await t.get(sessionRef);
    if (!sSnap.exists()) {
      return;
    }
    const s = sSnap.data();
    const sid = s.sessionId || '';

    if (sid !== sessionIdAtStart) {
      t.set(sessionRef, { active: false }, { merge: true });
      return;
    }

    if (s.awardedForSessionId === sid) {
      t.set(sessionRef, { active: false }, { merge: true });
      return;
    }

    const winRefs = topTwo.map((w) => doc(db, 'games', gameId, 'selectOMaticWins', w.teamId));
    const winSnaps = await Promise.all(winRefs.map((ref) => t.get(ref)));
    const prevs = winSnaps.map((ws) => (ws.exists() ? Number(ws.data().wins || 0) : 0));

    topTwo.forEach((w, i) => {
      t.set(winRefs[i], {
        teamId: w.teamId,
        teamName: w.teamName,
        wins: prevs[i] + 1,
        updatedAt: serverTimestamp(),
      });
    });

    t.set(
      sessionRef,
      {
        active: false,
        awardedForSessionId: sid,
      },
      { merge: true }
    );
  });

  return { awarded: topTwo };
}

export async function recordDeclined(db, gameId, teamId, teamName, sessionId) {
  const participantRef = participantDocRef(db, gameId, teamId);
  await setDoc(
    participantRef,
    {
      sessionId,
      teamId,
      teamName: teamName || 'Team',
      declined: true,
      cards: [],
      chosenCard: null,
      chosenSlot: null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Atomically draw two distinct cards for a team from the session deck.
 */
export async function dealParticipantCards(db, gameId, teamId, teamName, sessionId) {
  const sessionRef = sessionDocRef(db, gameId);
  const participantRef = participantDocRef(db, gameId, teamId);

  return runTransaction(db, async (transaction) => {
    const sessionSnap = await transaction.get(sessionRef);
    if (!sessionSnap.exists()) {
      throw new Error('Select-o-Matic is not available.');
    }
    const s = sessionSnap.data();
    if (!s.active || s.sessionId !== sessionId) {
      throw new Error('This selection round is closed.');
    }

    const partSnap = await transaction.get(participantRef);
    if (partSnap.exists()) {
      const p = partSnap.data();
      if (p.sessionId === sessionId && Array.isArray(p.cards) && p.cards.length === 2 && !p.declined) {
        return { cards: p.cards, alreadyDealt: true };
      }
    }

    let deck = Array.isArray(s.deck) ? [...s.deck] : [];
    if (deck.length < 2) {
      throw new Error('The deck has run out of cards.');
    }
    const c2 = deck.pop();
    const c1 = deck.pop();

    transaction.update(sessionRef, { deck });
    transaction.set(participantRef, {
      sessionId,
      teamId,
      teamName: teamName || 'Team',
      declined: false,
      cards: [c1, c2],
      chosenCard: null,
      chosenSlot: null,
      updatedAt: serverTimestamp(),
    });

    return { cards: [c1, c2], alreadyDealt: false };
  });
}

export async function lockParticipantChoice(db, gameId, teamId, sessionId, slotIndex) {
  const participantRef = participantDocRef(db, gameId, teamId);

  await runTransaction(db, async (transaction) => {
    const partSnap = await transaction.get(participantRef);
    if (!partSnap.exists()) {
      throw new Error('No entry found for this team.');
    }
    const p = partSnap.data();
    if (p.sessionId !== sessionId) {
      throw new Error('Session mismatch.');
    }
    if (p.chosenSlot != null || p.chosenCard != null) {
      return;
    }
    const cards = p.cards;
    if (!Array.isArray(cards) || cards.length !== 2) {
      throw new Error('Cards not dealt.');
    }
    if (slotIndex !== 0 && slotIndex !== 1) {
      throw new Error('Invalid card choice.');
    }
    const chosenCard = cards[slotIndex];
    transaction.update(participantRef, {
      chosenSlot: slotIndex,
      chosenCard,
      updatedAt: serverTimestamp(),
    });
  });
}
